import { defaultColumn } from '@components/cars-table-next/columns';
import { Cell, Column, Row, SortingFn, VisibilityState } from '@tanstack/react-table';
import { find, gt, isArray, isNumber, isObject, isString, lt } from 'lodash';
import { CSSProperties } from 'react';
import {
  ConditionalFormattingConditionTypes,
  ConditionalFormattingDTO,
  ConditionalFormattingDetailDTO,
  ConditionalFormattingScope,
  MonetaryAmount,
  UnitEntry,
} from 'src/modules/generated/api';

import {
  BidsTableNextItem,
  CarsTableNextItem,
  ConditionalFormatDef,
  ConditionalFormatKey,
  PercentEntry,
  StrictColumnDef,
} from './types';

const repeatingLinearGradient = ({
  colors,
  toDirection,
  fallback,
}: {
  colors: string[];
  toDirection?: string;
  fallback?: string;
}) => {
  const colorStops = colors.map((color, index) => `${color} ${index * 24}px, ${color} ${(index + 1) * 24}px`);
  return {
    backgroundImage: `repeating-linear-gradient(${toDirection}, ${colorStops.join(',')})`,
    backgroundColor: fallback,
  };
};

export const isMonetaryAmount = (x: unknown): x is MonetaryAmount => isObject(x) && 'currency' in x;

export const isUnitEntry = (x: unknown): x is UnitEntry => isObject(x) && 'unit' in x;

export const isPercentEntry = (x: unknown): x is PercentEntry => isObject(x) && 'percent' in x;

export const isAbsTaxEntry = (x: unknown): x is { total: MonetaryAmount } => isObject(x) && 'total' in x;

export const isDefined = <T>(x: T | boolean): x is T => typeof x !== 'boolean';

/**
 * Serialize conditional format keys from column definitions.
 */
export const getConditionalFormatKey = (conditionalFormatDef: ConditionalFormatDef): ConditionalFormatKey =>
  isString(conditionalFormatDef) ? conditionalFormatDef : conditionalFormatDef.key;

/**
 * Get typed and simplified value for condition operations.
 */
export const getCompareValue = (value: unknown): number | string | string[] | undefined => {
  if (isMonetaryAmount(value)) {
    return value.amount;
  }
  if (isUnitEntry(value)) {
    return value.value;
  }
  if (isPercentEntry(value)) {
    return value.percent;
  }
  if (isAbsTaxEntry(value)) {
    return value.total.amount;
  }
  if (isNumber(value)) {
    return value;
  }
  if (isArray(value)) {
    return value;
  }
  if (isString(value)) {
    return value;
  }
  return undefined;
};

/**
 * Test if a certain condition is matching the cell value
 */
const isConditionMatching = (value: unknown, formatCondition: ConditionalFormattingDetailDTO): boolean => {
  const compareValue = getCompareValue(value);

  if (isNumber(compareValue)) {
    if (formatCondition.gt !== undefined && formatCondition.lt !== undefined) {
      return gt(compareValue, formatCondition.gt) && lt(compareValue, formatCondition.lt);
    }
    if (formatCondition.gt !== undefined) {
      return gt(compareValue, formatCondition.gt);
    }
    if (formatCondition.lt !== undefined) {
      return lt(compareValue, formatCondition.lt);
    }
  }

  if (isString(compareValue)) {
    if (formatCondition.eq) {
      return compareValue === formatCondition.eq;
    }
    if (formatCondition.contains) {
      const pattern = Array.isArray(formatCondition.contains)
        ? formatCondition.contains.join('|')
        : formatCondition.contains;
      return new RegExp(pattern, 'ig').test(compareValue);
    }
  }

  if (isArray(compareValue)) {
    if (formatCondition.contains !== undefined) {
      if (formatCondition.condition === ConditionalFormattingConditionTypes.ContainsNot) {
        return !compareValue.some((item) => formatCondition.contains?.includes(item));
      }
      return compareValue.some((item) => formatCondition.contains?.includes(item));
    }
  }

  return false;
};

/**
 * Get all active matching format conditions for a certain cell
 */
const getMatchingFormatConditions = (
  cell: Cell<CarsTableNextItem, unknown>,
  conditionalFormats: ConditionalFormattingDTO[],
): ConditionalFormattingDetailDTO[] | undefined => {
  const conditionalFormatDef = cell.column.columnDef.meta?.conditionalFormat;

  if (!conditionalFormatDef) {
    return undefined;
  }

  const conditionalFormatDefs = Array.isArray(conditionalFormatDef) ? conditionalFormatDef : [conditionalFormatDef];

  // Support display-cells which might combine multiple properties.
  // E.g. Damage-Cell contains `priceDamage` + `damageType`
  const reducedMatchingConditions = conditionalFormatDefs.reduce<ConditionalFormattingDetailDTO[]>((acc, def) => {
    const value = isObject(def) && def.getValue ? def.getValue(cell) : cell.getValue();

    const formatKey = getConditionalFormatKey(def);
    const conditionalFormat = find(conditionalFormats, { field: formatKey });
    const conditions = conditionalFormat?.conditions ?? [];
    const matchingConditions = conditions.filter((condition) => isConditionMatching(value, condition));

    return [...acc, ...matchingConditions];
  }, []);

  return reducedMatchingConditions;
};

export const getMatchingRowFormatConditions = (
  row: Row<CarsTableNextItem>,
  conditionalFormats: ConditionalFormattingDTO[],
): ConditionalFormattingDetailDTO[] =>
  row
    .getAllCells()
    .reduce<ConditionalFormattingDetailDTO[]>(
      (acc, cell) => [...acc, ...(getMatchingFormatConditions(cell, conditionalFormats) ?? [])],
      [],
    )
    .filter((condition) => condition.scope === ConditionalFormattingScope.Row);

const getBackgroundStyles = (conditions: ConditionalFormattingDetailDTO[]): CSSProperties | undefined => {
  const resetStyles = {
    '--text_cell__text_color___text_solo': '',
  } as CSSProperties;

  if (conditions === undefined || conditions.length === 0) {
    return undefined;
  }

  if (conditions.length > 1) {
    return {
      ...resetStyles,
      ...repeatingLinearGradient({
        colors: conditions.map((c) => c.backgroundColor ?? '#fff'),
        toDirection: '135deg',
      }),
    };
  }

  return {
    ...resetStyles,
    background: conditions[0].backgroundColor,
  };
};

/**
 * Cell background resolution — combining row and cell conditional formatting.
 *
 * Scopes:
 *   - Row-scoped:  sets `--row-background` CSS var on the row div; TableCell reads it.
 *   - Cell-scoped: sets an inline background that overrides the CSS var.
 *
 * When both are active, these helpers combine all active colors into one diagonal stripe.
 *
 * Key behaviours:
 *   - Multiple cell conditions (e.g. damage: PriceDamage + DamageType) → N+1-band stripe.
 *   - Multiple row conditions → `--row-background` is already a gradient; extracted and merged.
 *   - Hardcoded tableCellProps bg (e.g. purchaseGross) + row condition, no cell condition →
 *     returns `{ background: undefined }` to suppress the hardcoded bg so the CSS var shows.
 *   - Row-scoped conditions are excluded from `getCellConditionalFormatColors` because
 *     their color is already in `rowBg`; including them would produce a same-color stripe.
 *
 * Spread order at call sites: cellProps.style → cellFormatStyles → resolveCellBackgroundStyles.
 */
export const getCellConditionalFormatColors = (
  cell: Cell<CarsTableNextItem, unknown>,
  conditionalFormats: ConditionalFormattingDTO[],
): string[] => {
  const conditions = getMatchingFormatConditions(cell, conditionalFormats);
  return (
    conditions
      ?.filter((c) => c.scope !== ConditionalFormattingScope.Row)
      .map((c) => c.backgroundColor)
      .filter((c): c is string => !!c) ?? []
  );
};

/**
 * Extract individual colors from a repeating-linear-gradient string produced by
 * repeatingLinearGradient(). Each color appears twice (start/end of its 24px band).
 * Returns empty array if the string is not a recognisable gradient.
 *
 * Note: only works for colors that contain no commas (hex, named, rgb() without spaces).
 * rgba()/hsl() colors with commas are not supported and will fall back to [rowBg].
 */
const extractGradientColors = (gradient: string): string[] => {
  const match = gradient.match(/^repeating-linear-gradient\([^,]+,(.+)\)$/s);
  if (!match) return [];
  const stops = match[1].split(',').map((s) => s.trim());
  const colors: string[] = [];
  for (let i = 0; i < stops.length; i += 2) {
    const color = stops[i].replace(/\s+\d+px$/, '').trim();
    if (color) colors.push(color);
  }
  return colors.length > 0 ? colors : [];
};

export const resolveCellBackgroundStyles = (rowBg: string | undefined, cellColors: string[]): CSSProperties => {
  if (rowBg && cellColors.length > 0) {
    const rowColors = rowBg.includes('gradient') ? extractGradientColors(rowBg) : null;
    return repeatingLinearGradient({ colors: [...(rowColors ?? [rowBg]), ...cellColors], toDirection: '135deg' });
  }
  if (rowBg) return { background: undefined };
  return {};
};

/**
 * Get cell scoped styles from conditional formatting.
 */
export const getConditionalFormatStyles = (
  cell: Cell<CarsTableNextItem, unknown>,
  conditionalFormats: ConditionalFormattingDTO[],
): CSSProperties | undefined => {
  const matchingConditions = getMatchingFormatConditions(cell, conditionalFormats);

  return {
    ...(matchingConditions && getBackgroundStyles(matchingConditions)),
  };
};

export const getConditionalRowFormatStyles = (
  row: Row<CarsTableNextItem>,
  conditionalFormats: ConditionalFormattingDTO[],
): CSSProperties | undefined => {
  const matchingConditions = getMatchingRowFormatConditions(row, conditionalFormats);

  return {
    ...(matchingConditions && getBackgroundStyles(matchingConditions)),
  };
};

/**
 * Retrieve initial visibility state from column definitions.
 */
export const getInitialColumnVisibility = (
  columns: StrictColumnDef<CarsTableNextItem | BidsTableNextItem>[],
): VisibilityState =>
  columns.reduce(
    (visibility, columnDef) => ({
      ...visibility,
      [columnDef.id]: columnDef.meta?.columnVisible ?? !!defaultColumn.meta?.columnVisible,
    }),
    {},
  );

/**
 * Retrieve initial visibility state from column definitions for Customer Mode,
 * where `isCustomerModeHidden` columns default to hidden and `isCustomerModeVisible` columns to visible.
 */
export const getInitialCustomerModeColumnVisibility = (
  columns: StrictColumnDef<CarsTableNextItem | BidsTableNextItem>[],
): VisibilityState => {
  const visibility = getInitialColumnVisibility(columns);
  return columns.reduce(
    (vis, columnDef) => ({
      ...vis,
      [columnDef.id]: columnDef.meta?.isCustomerModeHidden
        ? false
        : columnDef.meta?.isCustomerModeVisible || vis[columnDef.id],
    }),
    visibility,
  );
};

export const getAllExportableColumns = (columns: Column<CarsTableNextItem | BidsTableNextItem, unknown>[]) =>
  columns.filter((column) => column.columnDef.meta?.export?.enabled !== false);

export const getInternalColumns = (columns: Column<any, unknown>[]) =>
  columns.filter((colum) => !colum.columnDef.meta?.isExternalColumn);

export const getExternalColumns = (columns: Column<any, unknown>[]) =>
  columns.filter((colum) => colum.columnDef.meta?.isExternalColumn === true);

export const getColumnSortKey = (id: string, columns: StrictColumnDef<any>[]): string | null => {
  const column = columns.find((col) => col.id === id);

  if (!column) {
    return null;
  }

  return column.meta?.sortKey ?? id;
};

/**
 * Client-side sorting logic for tables that sort locally.
 * Reusing getCompareValue to resolve MonetaryAmount/UnitEntry/PercentEntry/
 * AbsTaxEntry cell values to a comparable primitive. Missing values always sort last, regardless
 * of sort direction.
 */
export const compareCellValuesForSort: SortingFn<CarsTableNextItem> = (rowA, rowB, columnId) => {
  const a = getCompareValue(rowA.getValue(columnId));
  const b = getCompareValue(rowB.getValue(columnId));
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  if (isNumber(a) && isNumber(b)) return a - b;
  const aStr = isArray(a) ? a.join(',') : String(a);
  const bStr = isArray(b) ? b.join(',') : String(b);
  return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
};
